/**
 * Las decisiones del buscador global, sin React.
 *
 * Viven aca y no adentro del componente porque son las que cambian el
 * comportamiento del sistema entero: a donde lleva cada resultado, y en que
 * orden se recorren con el teclado. Un error ahi no se ve en pantalla —la lista
 * se dibuja igual—, se ve cuando alguien elige una fila y termina en otro lado.
 */
import type { Proveedor } from "@/lib/tipos";

/** Minimo de letras para que el buscador haga algo. Con una sola, todo matchea. */
export const MINIMO = 2;

/** Cuantos proveedores se muestran. El resto se encuentra afinando el texto. */
export const TOPE_PROVEEDORES = 4;

/**
 * Los proveedores se filtran en memoria, no en el backend: son decenas y la
 * lista ya viene cacheada para los desplegables de compras y de productos.
 * Una consulta menos por cada tecla.
 */
export function filtrarProveedores(
  lista: readonly Proveedor[],
  texto: string,
  tope: number = TOPE_PROVEEDORES,
): Proveedor[] {
  const q = texto.trim().toLowerCase();
  if (q.length < MINIMO) return [];
  return lista.filter((p) => p.nombre.toLowerCase().includes(q)).slice(0, tope);
}

/**
 * Donde arranca cada seccion dentro de la lista plana que recorre el teclado.
 *
 * Se calcula en un solo lugar porque antes cada seccion sumaba a mano las
 * anteriores, y agregar una seccion en el medio desalineaba en silencio la
 * fila marcada: la flecha se movia a un lugar y el resaltado aparecia en otro.
 */
export function comienzosDeSeccion(cantidades: {
  productos: number;
  clientes: number;
  proveedores: number;
  ordenes?: number;
}): { clientes: number; proveedores: number; ordenes: number; comprobantes: number } {
  const clientes = cantidades.productos;
  const proveedores = clientes + cantidades.clientes;
  const ordenes = proveedores + cantidades.proveedores;
  const comprobantes = ordenes + (cantidades.ordenes ?? 0);
  return { clientes, proveedores, ordenes, comprobantes };
}

export type TipoResultado = "producto" | "cliente" | "proveedor" | "orden" | "comprobante";

export type Destino =
  | {
      to: "/catalogo-interno" | "/clientes" | "/proveedores" | "/reparaciones";
      search: { id: number } | { ot: number };
    }
  | { to: "/ventas"; search: { q: string } };

/**
 * A donde lleva un resultado.
 *
 * Un producto es el unico que depende de donde esta parado el usuario: en el
 * mostrador se agrega al ticket y no se navega a ningun lado, asi que devuelve
 * null y el que llama decide. En cualquier otra pantalla abre su ficha, que es
 * para lo que se busca cuando no se esta vendiendo.
 */
export function destinoDe(tipo: TipoResultado, id: number, modoVenta: boolean): Destino | null {
  if (tipo === "producto") return modoVenta ? null : { to: "/catalogo-interno", search: { id } };
  if (tipo === "cliente") return { to: "/clientes", search: { id } };
  if (tipo === "proveedor") return { to: "/proveedores", search: { id } };
  return { to: "/reparaciones", search: { ot: id } };
}

/**
 * Un comprobante abre Ventas buscando su numero: la busqueda de esa pantalla
 * ignora el periodo, asi que lo encuentra aunque sea viejo.
 */
export function destinoDeComprobante(numero: string): Destino {
  return { to: "/ventas", search: { q: numero } };
}

/**
 * Si vale la pena preguntar por comprobantes: solo si lo escrito tiene un
 * numero («VENTA-0000103», «0000099», «nc 3»). Buscando un nombre, `/ventas`
 * traeria todas las ventas de ese cliente y taparian lo que se busco.
 */
export function buscaComprobante(texto: string): boolean {
  return /[0-9]/.test(texto);
}

/**
 * Lo que dice el pie para Enter. En el mostrador solo un PRODUCTO se agrega a
 * la venta; un cliente, un proveedor o una orden abren su ficha igual. El pie
 * decia «agregar a la venta» sobre un cliente y Enter llevaba a su ficha.
 */
export function textoEnter(modoVenta: boolean, tipoActivo: TipoResultado | undefined): string {
  if (tipoActivo === "producto") {
    // Fuera del mostrador un producto abre el editor de costo y precio, no una
    // ficha de consulta: el pie decía «abrir la ficha» y se entraba a editar.
    return modoVenta ? "agregar a la venta" : "editar el producto";
  }
  return "abrir la ficha";
}

/**
 * El texto que copia «Copiar» para mandar por WhatsApp.
 *
 * `texto_whatsapp` sale de la base con el nombre pelado: dos usados del mismo
 * modelo (Deep Blue con 100 % de bateria y Silver con 91 %) copiaban el mismo
 * texto, y el cliente no podia saber de cual se hablaba. Se reemplaza el
 * nombre por el mismo nombre completo que se ve en la fila, con color y
 * bateria.
 */
export function textoParaCopiar(
  p: { nombre: string; texto_whatsapp: string },
  nombreCompleto: string,
): string {
  if (!nombreCompleto || !p.texto_whatsapp.startsWith(p.nombre)) return p.texto_whatsapp;
  return nombreCompleto + p.texto_whatsapp.slice(p.nombre.length);
}
